import * as React from 'react';
import { ColorsPicker, ColorsPickerProps } from '@easyv/react-components';
import chroma from 'chroma-js';
import { Color, MultiColorValueMap, MultiTypeColor } from '@/type';

type ColorType = 'pure' | 'linear' | 'pures' | 'range';
type ColorValueMap = any;
type ColorValueObj = any;

type ColorsPickerType = ColorType | ColorType[];
interface ProColorsPickerProps<T extends ColorsPickerType = ColorsPickerType>
  extends Omit<ColorsPickerProps<T>, 'value' | 'onChange' | 'onChangeComplete'> {
  value: T extends ColorType ? MultiColorValueMap[T] : MultiTypeColor;
  onChange: (value: T extends ColorType ? MultiColorValueMap[T] : MultiTypeColor) => void;
  onChangeComplete: (value: T extends ColorType ? MultiColorValueMap[T] : MultiTypeColor) => void;
}

const engineColorToUIColor = (color: Color) => {
  return chroma([
    ...color.rgb.map((a) => (a *= 255)),
    color.alpha === undefined ? 1 : color.alpha,
  ]).hex('rgba');
};

const UIColorToEngineColor: (color: string) => Color = (color) => {
  const a = chroma(color.toLowerCase());
  return {
    rgb: a.rgb().map((c) => (c /= 255)) as [number, number, number],
    alpha: a.alpha(),
  };
};

const toUIType = (v: MultiColorValueMap[keyof MultiColorValueMap] | MultiTypeColor) => {
  if (!v) return undefined;
  if ('stops' in v) {
    const value = {
      stops: v.stops.map((a) => ({
        color: engineColorToUIColor(a.color),
        offset: a.offset,
      })),
      angle: v.angle,
      opacity: v.opacity,
    };
    return value;
  }

  if (Array.isArray(v)) {
    const value = v.map((a) => engineColorToUIColor(a));
    return value;
  }

  if ('rgb' in v) {
    return engineColorToUIColor(v);
  }

  const value = {
    type: v.type,
  } as ColorValueObj;

  if (v.pure) {
    value.pure = engineColorToUIColor(v.pure);
  }
  if (v.linear) {
    value.linear = {
      stops: v.linear.stops.map((a) => ({
        color: engineColorToUIColor(a.color),
        offset: a.offset,
      })),
      angle: v.linear.angle,
      opacity: v.linear.opacity,
    };
  }

  if (v.pures) {
    value.pures = v.pures.map((a) => engineColorToUIColor(a));
  }

  if (v.range) {
    value.range = v.range.map((a) => engineColorToUIColor(a)) as [string, string];
  }

  return value;
};

const toEngineType = (v: ColorValueMap[keyof ColorValueMap] | ColorValueObj) => {
  if (!v) return undefined;
  if (typeof v === 'string') {
    return UIColorToEngineColor(v);
  }
  if ('stops' in v) {
    return {
      stops: v.stops.map((a: any) => ({
        color: UIColorToEngineColor(a.color),
        offset: a.offset,
      })),
      angle: v.angle,
      opacity: v.opacity,
    };
  }

  if (Array.isArray(v)) {
    const value = v.map((a) => UIColorToEngineColor(a));
    return value;
  }

  const value = {
    type: v.type,
  } as MultiTypeColor;

  if (v.pure) {
    value.pure = UIColorToEngineColor(v.pure);
  }
  if (v.linear) {
    value.linear = {
      stops: v.linear.stops.map((a: any) => ({
        color: UIColorToEngineColor(a.color),
        offset: a.offset,
      })),
      angle: v.linear.angle,
      opacity: v.linear.opacity,
    };
  }

  if (v.pures) {
    value.pures = v.pures.map((a: any) => UIColorToEngineColor(a));
  }

  if (v.range) {
    value.range = v.range.map((a: any) => UIColorToEngineColor(a)) as [Color, Color];
  }

  return value;
};

/** 从ColorsPicker拿到的数据为sRGB色彩空间 */
export function ProColorsPicker<T extends ColorsPickerType = ColorsPickerType>(
  props: ProColorsPickerProps<T>,
) {
  const { value, onChange, onChangeComplete, ...rest } = props;
  const [internalValue, setInternalValue] = React.useState<
    T extends ColorType ? ColorValueMap[T] : ColorValueObj
  >(toUIType(value) as any);
  // console.log('color picker receive value', value);
  React.useEffect(() => {
    setInternalValue(toUIType(value) as any);
  }, [value]);

  return (
    <ColorsPicker
      value={internalValue}
      onChange={(v: any) => {
        onChange(toEngineType(v as any) as any);
      }}
      onChangeComplete={(v: any) => {
        onChangeComplete?.(toEngineType(v as ColorValueObj) as any);
      }}
      {...rest}
    />
  );
}
